import { useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { ArrowLeft, Plus, Trash2, Search } from "lucide-react"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { useQuote, useCreateQuote, useUpdateQuote, useUpdateQuoteItems } from "@/hooks/use-quotes"
import { useCompanies } from "@/hooks/use-companies"
import { useProducts } from "@/hooks/use-products"
import { ApiClientError } from "@/lib/api-client"
import type { QuoteItem } from "@/lib/types"

const quoteSchema = z.object({
  company_id: z.string().optional(),
  currency: z.string().optional(),
  issue_date: z.string().optional(),
  valid_until: z.string().optional(),
  notes: z.string().optional(),
  terms: z.string().optional(),
  recipient_name: z.string().optional(),
  recipient_email: z.string().optional(),
})

type QuoteForm = z.infer<typeof quoteSchema>

interface LineItem {
  key: string
  product_id?: string
  name: string
  description: string
  quantity: number
  unit: string
  unit_price_net: number
  discount_percent: number
  vat_rate: number
}

function newLineItem(): LineItem {
  return {
    key: crypto.randomUUID(),
    name: "",
    description: "",
    quantity: 1,
    unit: "szt.",
    unit_price_net: 0,
    discount_percent: 0,
    vat_rate: 23,
  }
}

function calcLineNet(item: LineItem): number {
  const base = item.quantity * item.unit_price_net
  return base * (1 - item.discount_percent / 100)
}

function calcLineGross(item: LineItem): number {
  return calcLineNet(item) * (1 + item.vat_rate / 100)
}

export function QuoteFormPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id
  const [error, setError] = useState<string | null>(null)
  const [productSearch, setProductSearch] = useState("")

  const { data: quoteData } = useQuote(id)
  const { data: companiesData } = useCompanies()
  const { data: productsData } = useProducts({ search: productSearch, is_active: "true", limit: 20 })
  const createMutation = useCreateQuote()
  const updateMutation = useUpdateQuote()
  const updateItemsMutation = useUpdateQuoteItems()

  const companies = companiesData?.data ?? []
  const products = productsData?.data ?? []

  const [items, setItems] = useState<LineItem[]>(() => {
    if (isEdit && quoteData?.data?.items) {
      return quoteData.data.items.map((qi: QuoteItem) => ({
        key: qi.id,
        product_id: qi.product_id || undefined,
        name: qi.name,
        description: qi.description || "",
        quantity: qi.quantity,
        unit: qi.unit,
        unit_price_net: qi.unit_price_net,
        discount_percent: qi.discount_percent,
        vat_rate: qi.vat_rate,
      }))
    }
    return [newLineItem()]
  })

  // Sync items when quote data loads for edit mode
  const [loadedQuoteId, setLoadedQuoteId] = useState<string | null>(null)
  if (isEdit && quoteData?.data && quoteData.data.id !== loadedQuoteId) {
    setLoadedQuoteId(quoteData.data.id)
    if (quoteData.data.items.length > 0) {
      setItems(quoteData.data.items.map((qi: QuoteItem) => ({
        key: qi.id,
        product_id: qi.product_id || undefined,
        name: qi.name,
        description: qi.description || "",
        quantity: qi.quantity,
        unit: qi.unit,
        unit_price_net: qi.unit_price_net,
        discount_percent: qi.discount_percent,
        vat_rate: qi.vat_rate,
      })))
    }
  }

  const today = new Date().toISOString().split("T")[0]
  const defaultValidUntil = new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0]

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<QuoteForm>({
    resolver: zodResolver(quoteSchema),
    values: isEdit && quoteData ? {
      company_id: quoteData.data.company?.id || "",
      currency: quoteData.data.currency,
      issue_date: quoteData.data.issue_date || today,
      valid_until: quoteData.data.valid_until || defaultValidUntil,
      notes: quoteData.data.notes || "",
      terms: quoteData.data.terms || "",
      recipient_name: quoteData.data.recipient_name || "",
      recipient_email: quoteData.data.recipient_email || "",
    } : {
      company_id: "",
      currency: "PLN",
      issue_date: today,
      valid_until: defaultValidUntil,
      notes: "",
      terms: "",
      recipient_name: "",
      recipient_email: "",
    },
  })

  const addItem = () => setItems([...items, newLineItem()])

  const removeItem = (key: string) => {
    if (items.length <= 1) return
    setItems(items.filter((i) => i.key !== key))
  }

  const updateItem = (key: string, field: keyof LineItem, value: string | number) => {
    setItems(items.map((i) => i.key === key ? { ...i, [field]: value } : i))
  }

  const addProductToItems = (product: { id: string; name: string; unit: string; net_price: number | null; vat_rate: number | null }) => {
    setItems([...items.filter(i => i.name !== ""), {
      key: crypto.randomUUID(),
      product_id: product.id,
      name: product.name,
      description: "",
      quantity: 1,
      unit: product.unit || "szt.",
      unit_price_net: product.net_price || 0,
      discount_percent: 0,
      vat_rate: product.vat_rate ?? 23,
    }])
    setProductSearch("")
  }

  const totalNet = items.reduce((sum, i) => sum + calcLineNet(i), 0)
  const totalVat = items.reduce((sum, i) => sum + (calcLineGross(i) - calcLineNet(i)), 0)
  const totalGross = totalNet + totalVat

  const onSubmit = async (data: QuoteForm) => {
    try {
      setError(null)
      const payload = {
        ...data,
        company_id: data.company_id || null,
        owner_id: undefined,
        items: items.filter(i => i.name).map((i) => ({
          product_id: i.product_id || null,
          name: i.name,
          description: i.description || null,
          quantity: i.quantity,
          unit: i.unit,
          unit_price_net: i.unit_price_net,
          discount_percent: i.discount_percent,
          vat_rate: i.vat_rate,
        })),
      }

      if (isEdit) {
        await updateMutation.mutateAsync({ id, data: payload })
        await updateItemsMutation.mutateAsync({
          id,
          items: items.filter(i => i.name).map((i) => ({
            product_id: i.product_id || null,
            name: i.name,
            description: i.description || null,
            quantity: i.quantity,
            unit: i.unit,
            unit_price_net: i.unit_price_net,
            discount_percent: i.discount_percent,
            vat_rate: i.vat_rate,
          })),
        })
        toast.success("Oferta zaktualizowana")
        navigate(`/quotes/${id}`)
      } else {
        const res = await createMutation.mutateAsync(payload)
        toast.success("Oferta utworzona")
        navigate(`/quotes/${res.data.id}`)
      }
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError("Wystąpił nieoczekiwany błąd")
    }
  }

  const inputClass = "h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm text-foreground outline-none"
  const numClass = "h-8 w-full rounded-md border border-input bg-transparent px-2 text-sm text-foreground text-right outline-none tabular-nums"

  const fmt = (n: number) => new Intl.NumberFormat("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)

  return (
    <div className="mx-auto max-w-[960px] space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/50">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-foreground">
          {isEdit ? "Edytuj ofertę" : "Nowa oferta"}
        </h1>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Quote details */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Dane oferty</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Kontrahent</label>
              <select {...register("company_id")} className={inputClass}>
                <option value="">— Brak —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Waluta</label>
              <select {...register("currency")} className={inputClass}>
                <option value="PLN">PLN</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Data wystawienia</label>
              <input {...register("issue_date")} type="date" className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Ważna do</label>
              <input {...register("valid_until")} type="date" className={inputClass} />
            </div>
          </div>
        </section>

        {/* Recipient */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Odbiorca</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Nazwa odbiorcy</label>
              <input {...register("recipient_name")} className={inputClass}
                placeholder="Firma lub osoba" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Email odbiorcy</label>
              <input {...register("recipient_email")} type="email" className={inputClass}
                placeholder="email@firma.pl" />
            </div>
          </div>
        </section>

        {/* Line items */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase text-muted-foreground">Pozycje</h2>
            <div className="relative">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    placeholder="Szukaj produktu..."
                    className="h-8 w-48 rounded-md border border-input bg-transparent pl-7 pr-3 text-xs text-foreground outline-none"
                  />
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addItem} className="h-8">
                  <Plus size={14} className="mr-1" /> Pozycja
                </Button>
              </div>
              {productSearch && products.length > 0 && (
                <div className="absolute right-0 top-full mt-1 z-50 w-72 rounded-lg border border-border bg-card shadow-lg">
                  {products.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => addProductToItems(p)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-muted/30"
                    >
                      <span className="text-foreground">{p.name}</span>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {p.net_price != null ? `${fmt(p.net_price)} zł` : "—"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-border">
            <table className="w-full">
              <thead>
                <tr className="bg-muted">
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Nazwa</th>
                  <th className="w-20 px-2 py-2 text-right text-xs font-medium text-muted-foreground">Ilość</th>
                  <th className="w-16 px-2 py-2 text-left text-xs font-medium text-muted-foreground">J.m.</th>
                  <th className="w-28 px-2 py-2 text-right text-xs font-medium text-muted-foreground">Cena netto</th>
                  <th className="w-20 px-2 py-2 text-right text-xs font-medium text-muted-foreground">Rabat %</th>
                  <th className="w-20 px-2 py-2 text-right text-xs font-medium text-muted-foreground">VAT %</th>
                  <th className="w-28 px-2 py-2 text-right text-xs font-medium text-muted-foreground">Netto</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.key} className="border-t border-border">
                    <td className="px-3 py-2">
                      <input
                        value={item.name}
                        onChange={(e) => updateItem(item.key, "name", e.target.value)}
                        className="h-8 w-full rounded-md border border-input bg-transparent px-2 text-sm text-foreground outline-none"
                        placeholder="Nazwa pozycji"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(item.key, "quantity", parseFloat(e.target.value) || 0)}
                        className={numClass}
                        min="0"
                        step="1"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        value={item.unit}
                        onChange={(e) => updateItem(item.key, "unit", e.target.value)}
                        className="h-8 w-full rounded-md border border-input bg-transparent px-2 text-sm text-foreground outline-none"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        value={item.unit_price_net}
                        onChange={(e) => updateItem(item.key, "unit_price_net", parseFloat(e.target.value) || 0)}
                        className={numClass}
                        min="0"
                        step="0.01"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        type="number"
                        value={item.discount_percent}
                        onChange={(e) => updateItem(item.key, "discount_percent", parseFloat(e.target.value) || 0)}
                        className={numClass}
                        min="0"
                        max="100"
                        step="1"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <select
                        value={item.vat_rate}
                        onChange={(e) => updateItem(item.key, "vat_rate", parseFloat(e.target.value))}
                        className="h-8 w-full rounded-md border border-input bg-transparent px-1 text-sm text-foreground text-right outline-none"
                      >
                        <option value="23">23%</option>
                        <option value="8">8%</option>
                        <option value="5">5%</option>
                        <option value="0">0%</option>
                      </select>
                    </td>
                    <td className="px-2 py-2 text-right">
                      <span className="text-sm font-medium tabular-nums text-foreground">
                        {fmt(calcLineNet(item))}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <button type="button" onClick={() => removeItem(item.key)}
                        className={`rounded p-1 transition-colors hover:bg-red-50 ${items.length <= 1 ? "text-muted-foreground" : "text-destructive"}`}
                        disabled={items.length <= 1}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="border-t border-border bg-muted px-3 py-3 flex justify-end">
              <div className="w-64 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Netto:</span>
                  <span className="font-medium tabular-nums text-foreground">{fmt(totalNet)} zł</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">VAT:</span>
                  <span className="tabular-nums text-muted-foreground">{fmt(totalVat)} zł</span>
                </div>
                <div className="flex justify-between border-t border-border pt-1">
                  <span className="font-semibold text-foreground">Brutto:</span>
                  <span className="font-semibold tabular-nums text-primary">{fmt(totalGross)} zł</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Notes */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Dodatkowe</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Uwagi</label>
              <textarea {...register("notes")} rows={3}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none"
                placeholder="Dodatkowe uwagi do oferty" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Warunki</label>
              <textarea {...register("terms")} rows={3}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none"
                placeholder="Warunki realizacji" />
            </div>
          </div>
        </section>

        {/* Actions */}
        <div className="flex justify-end gap-3 border-t border-border pt-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Anuluj
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Zapisywanie..." : "Zapisz ofertę"}
          </Button>
        </div>
      </form>
    </div>
  )
}
